import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from '../api/api';
import { useUploadAttachment } from '../api/File.hooks';
import { InputAcceptType, PresignedUrl } from '../api/File.interface';
import { Attachment } from '../attachment/Attachment.interface';
import { createProxyUrl } from '../../secure-portal/documents/Document.hook';
import { InlineFileValue } from './InlineFile';

export const imageExtensions: string[] = InputAcceptType.Images.split(',');

export interface FileUploadHookProps {
  uploadPath: string;
  inputType: string;
  fileValues: InlineFileValue[];
  alwaysClear?: boolean;
  onChange?: (values: InlineFileValue[], presignedUrl?: PresignedUrl) => void;
  onSuccess?: () => void;
}

export function useFileUpload(props: FileUploadHookProps) {
  const {
    uploadPath,
    inputType,
    fileValues,
    alwaysClear = false,
    onChange,
    onSuccess
  } = props;
  const multiple: boolean = true;
  const [
    { presignedUrl, progress, error },
    uploadFiles,
    uploadCallback,
    removeCallback
  ] = useUploadAttachment(uploadPath);
  const isLoading: boolean = progress > 0 && progress < 100;

  // start uploading file(s)
  const handleFile = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files || files.length === 0) {
        return;
      }
      // setTouched(true);
      if (!multiple && uploadFiles && uploadFiles.length > 0) {
        // if only one file allowed, remove it before uploading new file
        removeCallback(uploadFiles[0].file);
      }
      // clears all files to avoid duplicate uploads
      if (alwaysClear && uploadFiles && uploadFiles.length > 0) {
        uploadFiles.forEach(uploadFile => removeCallback(uploadFile.file));
      }

      const filesToUpload = [];
      // check if file extension is an accepted input type
      for (let i = 0; i < files.length; i++) {
        const fileExtension = files[i].name.match(/\.([^.]+)$/g);
        const acceptableFileExtension = inputType?.includes(
          fileExtension ? fileExtension[0] : ' '
        );
        if (acceptableFileExtension) {
          // if the file extension is acceptable, push to the filesToUpload array
          filesToUpload.push(files[i]);
        }
      }
      // if the filesToUpload array has at least one file, proceed to upload
      if (filesToUpload.length > 0) {
        uploadCallback(multiple ? filesToUpload : [filesToUpload[0]]);
      }
    },
    [
      uploadFiles,
      inputType,
      multiple,
      alwaysClear,
      removeCallback,
      uploadCallback
    ]
  );

  useEffect(() => {
    if (!uploadFiles || !presignedUrl) {
      return;
    }

    const value: InlineFileValue[] = uploadFiles.map(uploadFile => ({
      url: uploadFile.presignedResult?.destinationUrl,
      signedUrl: uploadFile.presignedResult?.signedUrl,
      name: uploadFile.file.name,
      size: uploadFile.file.size
    }));
    if (multiple) {
      value.push(
        ...fileValues.filter(
          value =>
            // remove any duplicate files
            !uploadFiles.some(uploadFile => value.name === uploadFile.file.name)
        )
      );
    }
    // set value
    onChange?.(value, presignedUrl);

    const completedFiles = uploadFiles.filter(
      uploadFile => uploadFile.isCompleted
    );
    if (
      uploadFiles.length > 0 &&
      completedFiles.length === uploadFiles.length
    ) {
      // submit once all files are uploaded
      onSuccess?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFiles, presignedUrl]);

  return { handleFile, presignedUrl, progress, isLoading, error };
}

export function getSrcFromText(value: string): string[] {
  // find all img src=""
  return value
    .split('<img')
    .map((imgStr: string) => imgStr.slice(0, imgStr.indexOf('>')))
    .join()
    .trim()
    .split('src="')
    .map((dirtyUrl: string) => {
      dirtyUrl = dirtyUrl.slice(0, dirtyUrl.indexOf('"')) || '';
      return dirtyUrl.trim();
    })
    .slice(1);
}

export function isProxyURL(url: string): boolean {
  return url.includes('files/proxy') && url.includes('storageKey');
}

// add api token to img urls in text for InlineHtml
export function formatImgURLs(
  text: string,
  removeToken: boolean = false
): string {
  // console.log('InlineHtml: formatImgURLs');
  // find all img src=""
  // only include proxy URLs
  const urls: string[] = getSrcFromText(text).filter(isProxyURL);
  const replaceArr: Array<{ from: string; to: string }> = [];

  for (const url of urls) {
    // convert &amp; to &
    const ampUrl = url.replace(/&amp;/g, '&');
    const paramsStr = ampUrl.split('?')[1] || '';

    // check if it has storageKey
    const params = new URLSearchParams(paramsStr);
    const storageKey: string | null = params.get('storageKey');
    if (typeof storageKey !== 'string') {
      continue;
    }

    // if has storage key, format url
    if (removeToken) {
      params.delete('token');
    } else {
      // change token to current user token
      params.set('token', api.getAccessToken() || '');
    }

    // create proxy url with base url that matches api url
    const proxyUrl = new URL('files/proxy', api.getBaseUrl());
    proxyUrl.search = params.toString();
    const newUrl = proxyUrl.toString();

    // only replace if different
    if (ampUrl !== newUrl) {
      replaceArr.push({ from: url, to: newUrl });
    }
  }
  for (const replace of replaceArr) {
    text = text.replace(replace.from, replace.to);
  }

  return text;
}

export function getExtensionFromContentType(contentType: string): string {
  const extension: string = (contentType.split('/')[1] || '').trim();
  return extension ? `.${extension}` : '';
}

export function filterImageAttachments(
  attachments: Attachment[]
): Attachment[] {
  return attachments.filter((attachment: Attachment) => {
    return imageExtensions.some((extension: string) => {
      return (
        attachment.url?.includes(extension) ||
        attachment.name?.includes(extension)
      );
    });
  });
}

// hook wrapper
export function useFilterImageAttachments(
  attachments: Attachment[]
): Attachment[] {
  return useMemo(() => filterImageAttachments(attachments), [attachments]);
}

const BASE64_MARKER: string = ';base64,';

// NOTE: taken from https://gist.github.com/kylefox/c09a7b738aacf088c2ec2b359ddcc5ea
export function useConvertDataURLToFile(): (
  dataURL: string,
  getFileName?: (mimeType: string) => string
) => File | undefined {
  // check if given URL looks like a base64-encoded URL
  const isDataURI = useCallback((url: string): boolean => {
    return url.split(BASE64_MARKER).length === 2;
  }, []);

  const dataURItoFile = useCallback(
    (
      dataURI: string,
      getFileName?: (mimeType: string) => string
    ): File | undefined => {
      if (!isDataURI(dataURI)) {
        return;
      }

      const [mimePart = '', contentPart = ''] = dataURI.split(BASE64_MARKER);
      // Format of a base64-encoded URL:
      // data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAYAAAAEOCAIAAAAPH1dAAAAK
      const mime: string = mimePart.split(':')[1];
      const filename: string =
        getFileName?.(mime) ||
        'dataURI-file-' + new Date().getTime() + '.' + mime.split('/')[1];
      const bytes: string = atob(contentPart);
      const writer: Uint8Array = new Uint8Array(new ArrayBuffer(bytes.length));

      for (let i: number = 0; i < bytes.length; i++) {
        writer[i] = bytes.charCodeAt(i);
      }

      return new File([writer.buffer], filename, { type: mime });
    },
    [isDataURI]
  );

  return dataURItoFile;
}

export function useResolvablePromise<ResolveType = any, RejectType = any>() {
  const resolveRef = useRef<(value: ResolveType) => void>();
  const rejectRef = useRef<(value: RejectType) => void>();

  const createPromise = useCallback(
    (
      callback?: (
        resolve: (value: ResolveType) => void,
        reject: (value: RejectType) => void
      ) => void
    ) => {
      return new Promise<ResolveType>((resolve, reject) => {
        resolveRef.current = resolve;
        rejectRef.current = reject;
        callback?.(resolve, reject);
      });
    },
    []
  );

  return { createPromise, resolveRef, rejectRef };
}

export interface AttachNewImagesHookProps {
  attachments: InlineFileValue[];
  onCompleteUpload?: () => void;
  onChangeAttachments?: (attachments: InlineFileValue[]) => void;
}

export function useAttachNewImages(props: AttachNewImagesHookProps) {
  const { attachments, onCompleteUpload, onChangeAttachments } = props;

  // get all src from value
  // get storageKeys from src
  // check if storageKey exists in attachments
  // if storageKey exists but does not exist for attachments, reupload the image
  // if no storageKey, upload the image src

  const uploadedBlacklist = useRef<string[]>([]);
  const convertToFile = useConvertDataURLToFile();

  interface URLData {
    url: string;
    originalUrl: string;
    storageKey?: string;
  }

  const valueRef = useRef<
    { value: string; uploadData: URLData[] } | undefined
  >();

  const { createPromise, resolveRef, rejectRef } = useResolvablePromise<
    string | undefined
  >();

  const { handleFile, isLoading, error } = useFileUpload({
    uploadPath: 'attachments',
    inputType: InputAcceptType.Images,
    fileValues: attachments,
    alwaysClear: true,
    onSuccess: () => {
      onCompleteUpload?.();
      resolveRef.current?.(valueRef.current?.value);
    },
    onChange: (attachments: InlineFileValue[], presignedUrl?: PresignedUrl) => {
      onChangeAttachments?.(attachments);
      if (!(presignedUrl && typeof valueRef.current !== 'undefined')) {
        return;
      }

      // skip if exists
      if (getExistingStorageKeys().includes(presignedUrl.storageKey)) {
        return;
      }

      // replace each existing url
      const data: URLData | undefined = valueRef.current.uploadData.find(
        (data: URLData) => !!valueRef.current?.value.includes(data.originalUrl)
      );
      if (data) {
        uploadedBlacklist.current.push(presignedUrl.storageKey);
        const url: URL = createProxyUrl(
          presignedUrl.storageKey || '',
          undefined,
          true
        );
        valueRef.current.value = valueRef.current.value.replace(
          data.originalUrl,
          url.toString()
        );
      }
    }
  });

  const imageAttachments: Attachment[] = useFilterImageAttachments(attachments);

  const getStorageKeyFromURL = useCallback((url: string):
    | string
    | undefined => {
    // if uploaded, get storageKey
    if (isProxyURL(url)) {
      // assume url has converted &amp; to &
      const paramsStr: string = url.split('?')[1] || '';
      const params: URLSearchParams = new URLSearchParams(paramsStr);
      const storageKey: string | null = params.get('storageKey');
      if (storageKey) {
        return storageKey;
      }
    }
  }, []);

  const getURLData = useCallback(
    (urls: string[]): URLData[] => {
      return urls.map((url: string) => {
        // convert &amp; to &
        const ampUrl: string = url.replace(/&amp;/g, '&');
        return {
          url: ampUrl,
          originalUrl: url,
          storageKey: getStorageKeyFromURL(ampUrl)
        } as URLData;
      });
    },
    [getStorageKeyFromURL]
  );

  const getExtensionFromContentType = useCallback(
    (contentType: string): string => {
      const extension: string = (contentType.split('/')[1] || '').trim();
      return extension ? `.${extension}` : '';
    },
    []
  );

  const getExistingStorageKeys = useCallback((): string[] => {
    // get existing storageKeys from imageAttachments
    let existingKeys: string[] = [];
    for (const attachment of imageAttachments) {
      if (attachment.url) {
        existingKeys.push(attachment.url);
      }
    }

    // include uploaded list to avoid duplicate upload
    existingKeys.push(...uploadedBlacklist.current);
    existingKeys = Array.from(new Set(existingKeys));

    return existingKeys;
  }, [imageAttachments]);

  const filterUploadData = useCallback(
    (urlData: URLData[]): URLData[] => {
      const existingStorageKeys: string[] = getExistingStorageKeys();
      return urlData.filter((urlData: URLData) => {
        // upload if no storageKey, or if not in existing storageKeys
        return (
          !urlData.storageKey ||
          !existingStorageKeys.includes(urlData.storageKey)
        );
      });
    },
    [getExistingStorageKeys]
  );

  const createFileName = useCallback(
    (
      mimeType: string,
      index: number
    ): {
      fileName: string;
      extension: string;
      timestamp: number;
    } => {
      const extension: string = getExtensionFromContentType(mimeType) || '.png';
      const timestamp: number = +new Date();
      const fileName: string = `IMG_${timestamp}-${index}${extension}`;
      return { fileName, extension, timestamp };
    },
    [getExtensionFromContentType]
  );

  const convertURLDataToFile = useCallback(
    async (urlData: URLData, index: number): Promise<File> => {
      // fetch and upload url data
      if (urlData.url.startsWith('http')) {
        const response: Response = await fetch(urlData.url);
        // get extension and create fileName
        const contentType: string =
          response.headers.get('content-type') || 'image/png';
        const { fileName, timestamp } = createFileName(contentType, index);
        const blob: Blob = await response.blob();
        return new File([blob], fileName, {
          type: contentType,
          lastModified: timestamp
        });
      }

      try {
        // is dataURL
        const file: File | undefined = convertToFile(
          urlData.url,
          (mimeType: string) => createFileName(mimeType, index).fileName
        );
        if (file) {
          return file;
        }
      } catch (_) {
        // ignore error
      }

      throw new Error(
        'Unable to display image. Consider uploading the image instead.'
      );
    },
    [createFileName, convertToFile]
  );

  const uploadImages = useCallback(
    async (urlData: URLData[]) => {
      const files: File[] = await Promise.all(
        urlData.map(convertURLDataToFile)
      );
      // upload files
      handleFile(files);
    },
    [handleFile, convertURLDataToFile]
  );

  const inspectAndUpload = useCallback(
    (value: string): Promise<string | undefined> => {
      // cancel previous call via rejection
      // handle undefined error to do nothing when catched
      rejectRef.current?.(undefined);
      const urls: string[] = getSrcFromText(value);
      const uploadData: URLData[] = filterUploadData(getURLData(urls));
      valueRef.current = { value, uploadData };
      return createPromise(async (resolve, reject) => {
        if (!uploadData.length) {
          return resolve(value);
        }
        try {
          await uploadImages(uploadData);
        } catch (error) {
          reject(error);
        }
      });
    },
    [rejectRef, getURLData, uploadImages, filterUploadData, createPromise]
  );

  useEffect(() => {
    if (error) {
      rejectRef.current?.(error);
    }
  }, [error, rejectRef]);

  return { inspectAndUpload, isLoading };
}

export function useImageReloader() {
  type Timer = ReturnType<typeof setTimeout>;
  const maxAttempts: number = 10;
  const timersRef = useRef<Timer[]>([]);
  const unmountedRef = useRef<boolean>(false);
  const [error, setError] = useState<string | undefined>();

  const handleImageError = (imageEl: HTMLImageElement) => {
    // if successful, skip
    // NOTE: taken from https://stackoverflow.com/a/1977898/7013346
    if (imageEl.complete && imageEl.naturalWidth !== 0) {
      return;
    }

    let tryCount: number = 0;
    let didLoad: boolean = false;
    imageEl.onload = () => {
      didLoad = true;
    };
    imageEl.onerror = () => {
      // reload image after a second
      const reload = setTimeout(() => {
        // after maxAttempts, stop trying and show error if failed
        if (tryCount >= maxAttempts) {
          if (!didLoad && !unmountedRef.current) {
            setError('Unable to load image.');
          }
          return;
        }
        tryCount++;
        // set image src to reload image
        // NOTE: taken from https://stackoverflow.com/a/55499609/7013346
        const { src = '' } = imageEl;
        imageEl.src = src;
        console.log(
          'InlineHtml ImageReloader. Reload attempt:',
          tryCount,
          'Image:',
          src
        );
      }, 1000);
      timersRef.current.push(reload);
    };
  };

  const reloadFailedImages = (iframe?: HTMLIFrameElement) => {
    const doc: Document | null | undefined = iframe?.contentDocument;
    if (!doc) {
      return;
    }
    setError(undefined);
    // console.log('InlineHtml: reloadFailedImages');
    Array.from(doc.querySelectorAll('img')).forEach(handleImageError);
  };

  useEffect(() => {
    return () => {
      // clear all timers
      unmountedRef.current = true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timers: Timer[] = timersRef.current;
      timers.splice(0, timers.length).forEach(clearTimeout);
    };
  }, []);

  return { error, reloadFailedImages };
}

// broken image styling
// NOTE: taken from https://stackoverflow.com/a/37192970/7013346
const imageStyle: string = `
img {
  position: relative;
}

img:after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  font-size: 15px;
  letter-spacing: 1px;
  background-color: #fff;
  color: rgba(0, 0, 0, 0.54);
  animation: animateLoading 1s infinite;
}

@keyframes animateLoading {
  0% { content: '...'; }
  50% { content: '.'; }
  100% { content: '..'; }
}
`;

// apply styling for failed images
export function applyImageStyling(iframe?: HTMLIFrameElement) {
  const doc: Document | null | undefined = iframe?.contentDocument;
  if (!doc) {
    return;
  }
  // style manually as this is inside an iframe
  const styleEl: HTMLStyleElement = document.createElement('style');
  styleEl.innerHTML = imageStyle;
  doc.head.appendChild(styleEl);
}
